/**
 * R7B — OwnerEventsPanel
 *
 * Read-only display of the ``owner_events`` durable business-event
 * ledger. Independent of ``email_log`` so a business event stays
 * visible even when the ``email_log`` write fails entirely (the I4
 * failure mode).
 *
 * No action buttons, no dismiss control, no retry control. Pending
 * rows display as pending.
 */
import { Bell } from "lucide-react";
import {
  EmptyState,
  Panel,
  StatusBadge,
  relativeTime,
} from "./uiKit";

const EVENT_LABELS = {
  paid_signup_active: "Paid signup activated",
  subscription_canceled: "Subscription canceled",
  payment_failed: "Payment failed",
  payment_recovered: "Payment recovered",
};

export function OwnerEventsPanel({ events }) {
  const rows = Array.isArray(events) ? events : [];
  return (
    <Panel className="p-6" testId="admin-overview-owner-events-panel">
      <header className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            Owner alerts
          </p>
          <h2
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            Recent events
          </h2>
        </div>
        <Bell
          size={16}
          className="text-slate-400"
          data-testid="admin-overview-owner-events-icon"
        />
      </header>
      {rows.length === 0 ? (
        <EmptyState
          title="No owner events yet"
          description="Signups, cancellations, and payment events will appear here as durable rows — even when their email delivery fails."
        />
      ) : (
        <ul
          className="space-y-3"
          data-testid="admin-overview-owner-events-list"
        >
          {rows.map((e) => {
            const label =
              EVENT_LABELS[e.event_type] ||
              (e.event_type || "").replace(/_/g, " ");
            const customerName =
              (e.summary_fields && e.summary_fields.customer_name) ||
              "(unknown)";
            return (
              <li
                key={e.id}
                className="rounded-lg border border-slate-100 p-3 hover:border-slate-200 transition-colors"
                data-testid={`admin-overview-owner-event-${e.id}`}
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="text-xs font-bold text-slate-700 uppercase tracking-[0.16em]">
                    {label}
                  </p>
                  <StatusBadge status={e.delivery_status} />
                </div>
                <p className="text-sm text-slate-900 truncate">
                  {customerName}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {relativeTime(e.occurred_at)}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

export default OwnerEventsPanel;
